export type MembersWithType<T> = keyof {
  [K in keyof T as T[K] extends boolean ? K : never]: T[K];
};

export function jsonClone<T extends object>(input: T): T {
  return JSON.parse(JSON.stringify(input));
}

export function toBoolRecord<Key extends string | number, Item extends object>(
  arr: Item[],
  itemToKey: (item: Item) => Key,
  itemToValue: (item: Item) => boolean = () => true,
): Partial<Record<Key, boolean>> {
  const result: Partial<Record<Key, boolean>> = {};

  for (const item of arr) {
    result[itemToKey(item)] = itemToValue(item);
  }

  return result;
}

/**
 * Create a new promise and return the resolve and reject functions.
 *
 * In the spirit of Promise.withResolvers, part of ES2024, see: https://github.com/tc39/proposal-promise-with-resolvers
 * For availability see: https://caniuse.com/mdn-javascript_builtins_promise_withresolvers
 */
export function promiseWithResolvers<T>(): {
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason: Error) => void;
  promise: Promise<T>;
} {
  let resolve: (value: T | PromiseLike<T>) => void = () => {};
  let reject: (reason: Error) => void = () => {};
  const promise = new Promise<T>((resolve_, reject_) => {
    resolve = resolve_;
    reject = reject_;
  });
  return { resolve, reject, promise };
}

// Set.intersection is not available until ES2025, so this is a simple replacement:
export function setIntersection<T>(a: Set<T>, b: Set<T>): Set<T> {
  const result = new Set<T>();

  for (const item of a) {
    if (b.has(item)) {
      result.add(item);
    }
  }

  return result;
}

// Set.symmetricDifference is not available until ES2025, so this is a simple replacement:
export function setSymmetricDifference<T>(a: Set<T>, b: Set<T>): Set<T> {
  const result = new Set<T>(a);

  for (const item of b) {
    if (a.has(item)) {
      result.delete(item);
    } else {
      result.add(item);
    }
  }

  return result;
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
